import { Fragment, useEffect, useMemo, useRef } from 'react';
import { formatIstLongDate, istDayKey, type Campaign, type Segment } from '@wa/shared';
import { BroadcastBubble, type Drill } from '@/broadcast/BroadcastBubble';
import { IconSend } from '@/broadcast/TemplatesModal';

/**
 * The middle column: one list, and every broadcast ever sent to it, as a thread.
 *
 * The reference's framing, and a good one: a broadcast list *is* a conversation
 * the business has with a group, so its history reads like a chat — each send an
 * outbound bubble in order, with what happened underneath. It replaces a table of
 * campaigns with a status column, where "what did we last say to these people" took
 * a filter and a sort.
 *
 * An empty list gets the reference's "Get started" card in place of an empty
 * ground, because an empty ground says nothing about what to do next and the
 * answer is always the same: put people in it.
 */
export function BroadcastThread({
  segment, campaigns, isLoading, membersOpen, onToggleMembers, onImport, onQuickImport, onSampleFile,
  onSend, onDrill, onBack,
}: {
  segment: Segment;
  campaigns: Campaign[];
  isLoading: boolean;
  membersOpen: boolean;
  onToggleMembers: () => void;
  onImport: () => void;
  onQuickImport: () => void;
  onSampleFile: () => void;
  onSend: () => void;
  onDrill: (c: Campaign, drill: Drill) => void;
  onBack?: () => void;
}) {
  const empty = !isLoading && campaigns.length === 0 && (segment.memberCount ?? 1) === 0;
  const count = segment.kind === 1 ? null : segment.memberCount ?? 0;

  // Oldest at the top, newest at the bottom, whatever order they arrived in — a
  // thread reads downward in time, and the API's order is not this screen's to
  // depend on.
  const stampOf = (c: Campaign) => c.startedAt ?? c.scheduledAt ?? c.createdAt;
  const ordered = useMemo(() => [...campaigns].sort((a, b) => stampOf(a).localeCompare(stampOf(b))), [campaigns]);

  // Bubbles sit at the right edge like any outbound message, not in a centred
  // column: on a wide monitor a centred column floats in the middle of the ground
  // and reads as a document, while the chat next door keeps its sends on the right.
  // Opens on the newest send, like a chat, and follows a new one in.
  const bottom = useRef<HTMLDivElement>(null);
  const newestId = ordered.length ? ordered[ordered.length - 1]!.id : 0;
  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }); }, [segment.id, newestId]);

  return (
    <section className="flex h-full min-h-0 flex-col bg-(--color-surface)">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-(--color-line) px-3">
        {onBack && (
          <button type="button" onClick={onBack} aria-label="Back to lists" className="tap -ml-1 grid size-9 place-items-center rounded-full text-(--color-muted) hover:bg-(--color-sunken)">
            <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="M12.5 4.5 7 10l5.5 5.5" {...s} strokeWidth={1.8} /></svg>
          </button>
        )}
        <span aria-hidden="true" className="grid size-9 shrink-0 place-items-center rounded-full bg-(--color-brand-wash) text-(--color-brand)">
          <IconGroup />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-body font-semibold">{segment.name}</span>
          <span className="block truncate text-small text-(--color-muted)">
            {count === null ? 'Rule-based list' : `${count.toLocaleString('en-IN')} ${count === 1 ? 'member' : 'members'}`}
          </span>
        </span>
        <button
          type="button"
          onClick={onImport}
          className="tap hidden items-center gap-1.5 rounded-(--radius-field) border border-(--color-line) bg-(--color-sunken) px-3 py-1.5 text-body font-semibold text-(--color-ink) hover:bg-(--color-surface) sm:flex"
        >
          <IconImport /> Import
        </button>
        <button
          type="button"
          onClick={onToggleMembers}
          aria-label={membersOpen ? 'Hide members' : 'Show members'}
          aria-expanded={membersOpen}
          className="tap grid size-9 place-items-center rounded-full text-(--color-ink-soft) hover:bg-(--color-sunken)"
        >
          <svg viewBox="0 0 20 20" className={`size-5 transition-transform ${membersOpen ? '' : 'rotate-180'}`} aria-hidden="true">
            <path d="m7.5 4.5 5.5 5.5-5.5 5.5" {...s} strokeWidth={1.8} />
          </svg>
        </button>
      </header>

      <div className="chat-ground pane-scroll relative min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {empty ? (
          <GetStarted onImport={onImport} onQuickImport={onQuickImport} onSampleFile={onSampleFile} />
        ) : (
          <div className="flex flex-col gap-3">
            {isLoading && <p className="text-center text-small text-(--color-muted)">Loading…</p>}
            {!isLoading && campaigns.length === 0 && (
              <p className="mx-auto rounded-(--radius-chip) bg-(--color-surface) px-3 py-1 text-small text-(--color-muted) shadow-sm">
                Nothing has been sent to this list yet.
              </p>
            )}
            {ordered.map((c, i) => {
              const stamp = stampOf(c);
              const prev = ordered[i - 1];
              const prevStamp = prev ? stampOf(prev) : null;
              const newDay = !prevStamp || istDayKey(prevStamp) !== istDayKey(stamp);
              return (
                <Fragment key={c.id}>
                  {newDay && (
                    <p className="mx-auto rounded-(--radius-chip) bg-(--color-surface) px-3 py-1 text-small font-semibold text-(--color-muted) shadow-sm">
                      {formatIstLongDate(stamp)}
                    </p>
                  )}
                  <BroadcastBubble campaign={c} onDrill={onDrill} />
                </Fragment>
              );
            })}
            <div ref={bottom} />
          </div>
        )}
      </div>

      <footer className="flex shrink-0 justify-end border-t border-(--color-line) px-4 py-2.5">
        <button
          type="button"
          onClick={onSend}
          className="tap flex items-center gap-2 rounded-(--radius-field) bg-(--color-brand) px-4 py-2.5 text-body font-semibold text-(--color-brand-ink) shadow-(--shadow-pop) hover:bg-(--color-brand-strong)"
        >
          <IconSend /> Send WhatsApp Template
        </button>
      </footer>
    </section>
  );
}

function GetStarted({ onImport, onQuickImport, onSampleFile }: { onImport: () => void; onQuickImport: () => void; onSampleFile: () => void }) {
  return (
    <div className="mx-auto mt-6 max-w-md">
      <div className="rounded-(--radius-panel) bg-(--color-surface) p-4 shadow-(--shadow-pop)">
        <h3 className="text-center text-lead font-semibold">Get started</h3>
        <p className="mt-0.5 text-center text-small text-(--color-muted)">Import your contacts to broadcast your first template</p>

        <div className="mt-4 flex flex-col items-center rounded-(--radius-field) border border-dashed border-(--color-line) bg-(--color-paper) px-4 py-5">
          <IconSpreadsheet />
          <p className="mt-3 text-body font-semibold">Drag and drop your contacts file</p>
          <p className="mt-0.5 text-small text-(--color-faint)">You can upload: xls, xlsx, csv, .ods</p>
          <button
            type="button"
            onClick={onImport}
            className="tap mt-3 rounded-(--radius-field) bg-(--color-brand) px-4 py-2 text-body font-semibold text-(--color-brand-ink) hover:bg-(--color-brand-strong)"
          >
            Upload from Computer
          </button>
          <button type="button" onClick={onSampleFile} className="tap mt-2 flex items-center gap-1.5 text-body font-medium text-(--color-ink) hover:underline">
            <IconFile /> Download sample file
          </button>
        </div>

        <p className="my-3 flex items-center gap-3 text-body text-(--color-faint)">
          <span className="h-px flex-1 bg-(--color-line)" />or<span className="h-px flex-1 bg-(--color-line)" />
        </p>

        <button
          type="button"
          onClick={onQuickImport}
          className="tap flex w-full items-center justify-center gap-2 rounded-(--radius-field) border border-(--color-line) px-4 py-2.5 text-body font-semibold text-(--color-ink) hover:bg-(--color-sunken)"
        >
          <span className="text-(--color-warn)"><IconBoltSmall /></span> Copy paste contacts for Quick Import
        </button>
      </div>
    </div>
  );
}

const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
const IconGroup = () => <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><circle cx="7.5" cy="7.5" r="2.6" {...s} /><path d="M3 16a4.5 4.5 0 0 1 9 0M13 5.5a2.4 2.4 0 0 1 0 4.6M14.5 16a4.3 4.3 0 0 0-1.4-3.1" {...s} /></svg>;
const IconImport = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M10 3v9M6.5 8.5 10 12l3.5-3.5M4 14v2.5h12V14" {...s} /></svg>;
const IconFile = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M5 3h7l4 4v10H5z" {...s} /><path d="M12 3v4h4" {...s} /></svg>;
const IconBoltSmall = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M11.5 2.5 5 11h4.2L8.5 17.5 15 9h-4.2z" fill="currentColor" /></svg>;
const IconSpreadsheet = () => (
  <svg viewBox="0 0 40 40" className="size-10" aria-hidden="true">
    <path d="M14 5h13l7 7v23H14z" fill="#fff" stroke="#B9B9B9" strokeWidth="1.5" />
    <path d="M27 5v7h7" fill="none" stroke="#B9B9B9" strokeWidth="1.5" />
    <rect x="5" y="13" width="18" height="18" rx="2" fill="#1D6F42" />
    <path d="m10 18 8 8M18 18l-8 8" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);
